/* eslint-disable camelcase */
import { z } from 'zod';

// Types
const PermissionsStruct = z.enum(['allow', 'deny', 'upsell']);
const ProductsStruct = z.object({
  productsCreditAgreementsPermissions: PermissionsStruct,
  productsReportsPermissions: PermissionsStruct,
  productsReportsReportsTabPermissions: PermissionsStruct,
  productsDataPipelinePermissions: PermissionsStruct,
  productsCalculationTerminologiesPermissions: PermissionsStruct,
  productsAssignmentsPermissions: PermissionsStruct,
  productsReceivablesPermissions: PermissionsStruct,
  productsFundingRequestsPermissions: PermissionsStruct,
  productsFundingRequestCancelApprovedPermissions: PermissionsStruct,
  productsInvoicesPermissions: PermissionsStruct,
  productsInvoicesEventLogPermissions: PermissionsStruct,
  productsInvoiceMechanicsPermissions: PermissionsStruct,
  productsInvoicesFullBalancePaymentPermissions: PermissionsStruct,
  productsLoansIbsIntegrationPermissions: PermissionsStruct,
  productsCollateralDataMonitoringPermissions: PermissionsStruct,
  productsStratsPermissions: PermissionsStruct,
  productsCustomerProvidedVariablesPermissions: PermissionsStruct,
  productsInsightsPermissions: PermissionsStruct,
  productsStatementsPermissions: PermissionsStruct,
  productsDeliverablesPermissions: PermissionsStruct,
  productsDeliverablesEventLogPermissions: PermissionsStruct,
  productsDeliverablesEventLogMultiEntityPermissions: PermissionsStruct,
  productsTransactionsPermissions: PermissionsStruct,
  productsTransactionsAdjustLiabilitiesPermissions: PermissionsStruct,
  productsLoansPermissions: PermissionsStruct,
  productsLoansEditPermissions: PermissionsStruct,
  productsEventsLogPermissions: PermissionsStruct,

  // the flags below are about implementation portal specific to
  // valley sls.
  productsImplementationPortalPermissions: PermissionsStruct,
  productsImplementationPortalDocumentUploadPermissions: PermissionsStruct,
  productsImplementationPortalCreditAgreementAutomationPermissions: PermissionsStruct,

  // this permissions flag is to control whether we show
  // implementations list and details page in core dashboard
  productsImplementationsOnCoreDashboard: PermissionsStruct,

  productsFundingRequestsVerificationsPermissions: PermissionsStruct,
  // borrower can create funding requests
  productsFundingRequestsCreatePermissions: PermissionsStruct,
  // lender can create funding requests
  productsFundingRequestsLenderCreatePermissions: PermissionsStruct,
  // borrower can upsize funding requests
  productsFundingRequestsUpsizePermissions: PermissionsStruct,
  // lender can upsize funding requests
  productsFundingRequestsLenderUpsizePermissions: PermissionsStruct,
  // borrower can cancel + duplicate funding requests (action on submitted/rejected create or upsize FR)
  productsFundingRequestsDuplicatePermissions: PermissionsStruct,
  // lender can cancel + duplicate funding requests (action on submitted/rejected create or upsize FR)
  productsFundingRequestsLenderDuplicatePermissions: PermissionsStruct,
  productsFundingRequestsEventLogPermissions: PermissionsStruct,
  productsFundingRequestsEventLogMultiEntityPermissions: PermissionsStruct,
  productsCreditFacilitiesPortfolioBorrowingBasePermissions: PermissionsStruct,
  productsCreditFacilitiesPortfolioFinancialsPermissions: PermissionsStruct,
  productsCreditFacilitiesPortfolioProfitLossPermissions: PermissionsStruct,
  productsSettingsFundingRequestsPermissions: PermissionsStruct,
  productsSettingsFundingRequestsInternalApprovalsPermissions: PermissionsStruct,
  productsAnalyticsPermissions: PermissionsStruct,
  productsAnalyticsRiskPermissions: PermissionsStruct,
  productsAnalyticsStratsPermissions: PermissionsStruct,
  productsAnalyticsReportBuilderPermissions: PermissionsStruct,
  productsTasksCenterPermissions: PermissionsStruct,
  productsLpDashboardPermissions: PermissionsStruct,
  productsLimitedPartnersEventLogPermissions: PermissionsStruct,

  // Valley SBL
  productsValleyBankLoanAccountsEventLogPermissions: PermissionsStruct,
});

export const FeatureFlagSetStruct = ProductsStruct.extend({
  // This is a utility flag. It is used to determine if the FeatureFlagProvider has successfully
  // fetched flags from LaunchDarkly at least once
  doNotRemoveFeatureFlagProviderReady: z.boolean(),

  assignByStrategy: z.boolean(),
  assignByIndividualReceivable: z.boolean(),
  displayPortfolioCovenants: z.boolean(),
  displayFinancialCovenantInsights: z.boolean(),
  // @jendevelops [frv2-TODO] - remove flag once FRv2 is GA
  displayFundingRequestV2: z.boolean(),
  displayFundingRequestV2ProviderV2: z.boolean(),
  displayFundingRequestV2Alpha: z.boolean(),
  fundingRequestV2AlphaDrawPermissions: z.enum([
    'globalOnly',
    'classOnly',
    'allow',
    'deny',
  ]),
  fundingRequestV2AlphaRecyclePermissions: z.boolean(),

  // facilities to hide in the dashboard
  creditFacilitySlugsToHide: z.array(z.string()),

  isStatementsMonthlySummaryEnabled: z.boolean(),
  displayLenderPortfolioMonitoringCovenants: z.boolean(),
  displayLenderPortfolioMonitoringReceivables: z.boolean(),
  displayLenderPortfolioMonitoringSummaryV2: z.boolean(),

  // Flag drives showing Explo Report Builder feature in respective areas.
  // Lender: Facility Portfolio Tab
  // Borrower: Strats Page
  showExploReportBuilder: z.boolean(),

  displayLenderSettingsFacilities: z.boolean(),
  displayBorrowerSettingsFacilities: z.boolean(),

  // should we let lender edit baseFields?
  enableLenderBaseFieldEdits: z.boolean(),

  // temporary flags until @JonAtFinley's work on uiVisibility is complete
  isBorrowerLimitedOrganization: z.boolean(),
  lenderOwnerOrganizationWithBorrowerLimited: z.boolean(),

  // v4 deliverables
  supportDeliverablesAsyncGeneration: z.boolean(),

  // SLS Agent (Lender) View
  isSlsAgentLender: z.boolean(),
  useMultiLoanOriginationRequest: z.boolean(),

  // VB Lender View (SBL - DO NOT EDIT USAGE WITHOUT CONSULTING SOMEONE WHO KNOWS SBL)
  isValleyBankLenderView: z.boolean(),

  // Target server
  targetHeaderValue: z.string(),

  isAthenaEnabled: z.boolean(),
  isExcelEngineAdvanceRateEnabled: z.boolean(),

  // Temporary flag for expense authoring rollout - TODO: @sachinr4 to deprecate
  allowExpenseAuthoring: z.boolean(),

  showBalanceSheetExposure: z.boolean(),

  // TODO: @haghanim deprecate. temporary for fin stmt dashboard
  isFinancialStatementsDashboardEnabled: z.boolean(),

  enableRailzIntegration: z.boolean(),

  useFrontendWaterfallWorkaround: z.boolean(),

  enableDeliverablesV5: z.boolean(),
  enableEventLogV2: z.boolean(),

  // when set to to true, borrowing base cards will use the format defined in the concentration limit
  // that is percentage or currency or number.
  respectConcentrationLimitFormat: z.boolean(),

  // when set to true, profit loss container will only show statements section
  isProfitLossStatementsOnlyEnabled: z.boolean(),

  // when set to true, FRv1 path will allow selecting dates in the past
  allowHistoricalFundingRequest: z.boolean(),
});
export type FeatureFlagSet = z.infer<typeof FeatureFlagSetStruct>;

// Constants
/**
 * REMINDER: when updating this list, please also update "FeatureFlagSet" types above
 *
 * We should look to improve process of setting flag types and defaults.
 * Initially basing off of below (dated) issue on one avenue of managing this.
 * https://github.com/launchdarkly/js-sdk-common/issues/32
 *
 * One key downfall of this method is that it doesn't prevent users to add / access
 * flags that aren't included in the type. So if type doesn't include "unavailable",
 * compiler won't complain about "flags.unavailable" (it'll treat it as "any" type),
 * or any lingering flags as well.
 * This possibly may be resolved once we update our LD package version.
 *
 * Note: at the moment, we use a single instance of LaunchDarkly for both our
 * "admin" and "core" apps, implying that both apps technically have access to
 * the same flags. If we do split up LD usage, should also split this structure
 */
export const defaultFeatureFlags: FeatureFlagSet = {
  /* Product permissions START */
  // These should ** always ** be 'deny'
  productsCreditAgreementsPermissions: 'deny',
  productsReportsPermissions: 'deny',
  productsReportsReportsTabPermissions: 'deny',
  productsDataPipelinePermissions: 'deny',
  productsCalculationTerminologiesPermissions: 'deny',
  productsAssignmentsPermissions: 'deny',
  productsReceivablesPermissions: 'deny',
  productsFundingRequestsPermissions: 'deny',
  productsFundingRequestCancelApprovedPermissions: 'deny',
  productsInvoicesPermissions: 'deny',
  productsInvoicesEventLogPermissions: 'deny',
  productsInvoiceMechanicsPermissions: 'deny',
  productsInvoicesFullBalancePaymentPermissions: 'deny',
  productsLoansIbsIntegrationPermissions: 'deny',
  productsCollateralDataMonitoringPermissions: 'deny',
  productsStratsPermissions: 'deny',
  productsCustomerProvidedVariablesPermissions: 'deny',
  productsInsightsPermissions: 'deny',
  productsStatementsPermissions: 'deny',
  productsDeliverablesPermissions: 'deny',
  productsDeliverablesEventLogPermissions: 'deny',
  productsDeliverablesEventLogMultiEntityPermissions: 'deny',
  productsTransactionsPermissions: 'deny',
  productsTransactionsAdjustLiabilitiesPermissions: 'deny',
  productsLoansPermissions: 'deny',
  productsLoansEditPermissions: 'deny',
  productsEventsLogPermissions: 'deny',
  productsImplementationPortalPermissions: 'deny',
  productsImplementationPortalDocumentUploadPermissions: 'deny',
  productsImplementationPortalCreditAgreementAutomationPermissions: 'deny',
  productsImplementationsOnCoreDashboard: 'deny',
  productsFundingRequestsVerificationsPermissions: 'deny',
  productsFundingRequestsCreatePermissions: 'deny',
  productsFundingRequestsLenderCreatePermissions: 'deny',
  productsFundingRequestsUpsizePermissions: 'deny',
  productsFundingRequestsLenderUpsizePermissions: 'deny',
  productsFundingRequestsDuplicatePermissions: 'deny',
  productsFundingRequestsLenderDuplicatePermissions: 'deny',
  productsFundingRequestsEventLogPermissions: 'deny',
  productsFundingRequestsEventLogMultiEntityPermissions: 'deny',
  productsCreditFacilitiesPortfolioFinancialsPermissions: 'deny',
  productsCreditFacilitiesPortfolioProfitLossPermissions: 'deny',
  productsCreditFacilitiesPortfolioBorrowingBasePermissions: 'deny',
  productsSettingsFundingRequestsPermissions: 'deny',
  productsSettingsFundingRequestsInternalApprovalsPermissions: 'deny',
  productsAnalyticsPermissions: 'deny',
  productsAnalyticsRiskPermissions: 'deny',
  productsAnalyticsStratsPermissions: 'deny',
  productsAnalyticsReportBuilderPermissions: 'deny',
  productsTasksCenterPermissions: 'deny',
  productsLpDashboardPermissions: 'deny',
  productsLimitedPartnersEventLogPermissions: 'deny',
  productsValleyBankLoanAccountsEventLogPermissions: 'deny',
  /* Product permissions END */

  doNotRemoveFeatureFlagProviderReady: false,

  assignByStrategy: false,
  assignByIndividualReceivable: false,
  displayPortfolioCovenants: false,
  displayFinancialCovenantInsights: false,

  /**
   * @jendevelops [frv2-TODO] - remove flag once FRv2 is GA
   *
   * The following flags are used to control the display of the new Funding Request V2 views across
   * - /funding-requests/create
   * - /funding-requests/upsize
   * - /funding-requests/:id
   *
   * If none of these flags are turned on, customers will see the existing Funding Request V1 views
   *
   * The precedence order of these flags are
   * - displayFundingRequestV2ProviderV2
   *   shows the new wip version of frv2 that will use a new provider schema, only used by valley sls team for now
   * - displayFundingRequestV2Alpha
   *   shows the SubmitFundingRequestForAlphaCustomers view that is intended for alpha customers that have frv2 on in prod,
   *     separate from valley sls
   * - displayFundingRequestV2
   *   shows the new frv2 version 1 view
   * So that means that if you have multiple flags turned on, the one with the highest precedence will be displayed (early return in route)
   *
   * These flags are also to be used in tandem with the following product permissions to control the ability to create + upsize a FR
   * - productsFundingRequestsCreatePermissions
   * - productsFundingRequestsLenderCreatePermissions
   * - productsFundingRequestsUpsizePermissions
   * - productsFundingRequestsLenderUpsizePermissions
   *
   * If you have any questions about these flags, please reach out to @jendevelops
   */
  // this flag is used by most customers to display the new frv2 version 1 route
  displayFundingRequestV2: false,
  // this flag is used to display the frv2 version 2 view (most people should not need to use this outside of the valley team)
  displayFundingRequestV2ProviderV2: false,
  // this flag is used to display the frv2 form SubmitFundingRequestForAlphaCustomers
  displayFundingRequestV2Alpha: false,
  // this flag is used to control the permissions for the alpha frv2 form draw request input type
  fundingRequestV2AlphaDrawPermissions: 'deny',
  // this flag is used to control the permissions for the alpha frv2 form recycle request
  fundingRequestV2AlphaRecyclePermissions: false,

  isSlsAgentLender: false,
  useMultiLoanOriginationRequest: false,

  creditFacilitySlugsToHide: [],

  isStatementsMonthlySummaryEnabled: false,
  displayLenderPortfolioMonitoringCovenants: false,
  displayLenderPortfolioMonitoringReceivables: false,

  showExploReportBuilder: false,

  displayLenderSettingsFacilities: false,
  displayBorrowerSettingsFacilities: false,
  displayLenderPortfolioMonitoringSummaryV2: false,

  enableLenderBaseFieldEdits: false,

  isBorrowerLimitedOrganization: false,
  lenderOwnerOrganizationWithBorrowerLimited: false,

  supportDeliverablesAsyncGeneration: false,

  isValleyBankLenderView: false,

  targetHeaderValue: '',

  isAthenaEnabled: false,
  isExcelEngineAdvanceRateEnabled: false,

  allowExpenseAuthoring: false,

  showBalanceSheetExposure: false,

  // TODO: @haghanim deprecate. temporary for fin stmt dashboard
  isFinancialStatementsDashboardEnabled: false,

  enableRailzIntegration: false,

  useFrontendWaterfallWorkaround: false,

  enableDeliverablesV5: false,
  enableEventLogV2: false,
  respectConcentrationLimitFormat: false,
  isProfitLossStatementsOnlyEnabled: false,

  allowHistoricalFundingRequest: false,
};
